// Browser entry: wires the headless Game to storage, renderer, audio and UI.
import './view/style.css';
import { Game } from './game/game';
import { deserialize, SAVE_VERSION, serialize, type GameState } from './game/state';
import type { User } from 'firebase/auth';
import { Accounts, isVerifiedUser } from './platform/auth';
import { CloudSave, decideBootSave, saveOwner, setSaveOwner } from './platform/cloudSave';
import { Payments } from './platform/payments';
import { BackedUpSave, LocalSaveBackend } from './platform/save';
import { AccountUI } from './view/accountUi';
import { GemStore } from './view/gemStore';
import { playIntro } from './view/storyIntro';
import { Sfx } from './view/audio';
import { Renderer } from './view/renderer';
import { UI } from './view/ui';
import { MetaUI } from './view/metaUi';
import { celebrateGems } from './view/purchaseReward';
import { GAME_DIFFICULTY, difficultyOf, type DifficultyId } from './data/difficulty';

const SAVE_EVERY_MS = 5000;
/** How long boot waits for Firebase to restore a session before starting as a guest. */
const AUTH_WAIT_MS = 8000;
// The prototype saved here; migrated on first load (see migrations in state.ts).
const PROTOTYPE_KEY = 'tapheroes.save.v1';

// v2: choices made while comparing difficulties are dropped so everyone lands on GAME_DIFFICULTY.
const DIFFICULTY_KEY = 'tapheroes.difficulty.v2';

/** Shipped builds always run GAME_DIFFICULTY; the dev server honours the dev-only picker. */
function readDifficulty(): DifficultyId {
  if (!import.meta.env.DEV) return GAME_DIFFICULTY;
  try { return difficultyOf(localStorage.getItem(DIFFICULTY_KEY) ?? GAME_DIFFICULTY).id; } catch { return GAME_DIFFICULTY; }
}

/** Each difficulty has its own slot; Easy keeps the original key so existing saves carry over. */
function saveKeys(d: DifficultyId): { main: string; backup: string } {
  return d === 'easy'
    ? { main: 'tapheroes.save', backup: 'tapheroes.save.backup' }
    : { main: `tapheroes.save.${d}`, backup: `tapheroes.save.${d}.backup` };
}

async function loadState(store: BackedUpSave, difficulty: DifficultyId): Promise<GameState | undefined> {
  const now = Date.now();
  const candidates = [await store.load(), await store.loadBackup()];
  if (difficulty === 'easy') candidates.push(await new LocalSaveBackend(PROTOTYPE_KEY).load());
  for (const raw of candidates) {
    if (!raw) continue;
    try { return deserialize(raw, now); } catch (e) { console.warn('Skipping unreadable save:', e); }
  }
  return undefined;
}

/** Keeps a save that's being replaced on this device (never deleted), under a side key. */
async function setAside(store: BackedUpSave, mainKey: string): Promise<void> {
  const raw = await store.load();
  if (raw) await new LocalSaveBackend(`${mainKey}.kept`).save(raw);
}

/**
 * Which save this session plays. Guests play this browser's save. A signed-in player's save is
 * matched against their cloud save (see decideBootSave); a guest's progress may be adopted into
 * the account, and whatever loses is kept aside on this device.
 */
async function chooseSave(local: GameState | undefined, account: User | null, cloud: CloudSave, accountUi: AccountUI,
  store: BackedUpSave, mainKey: string): Promise<GameState | undefined> {
  const owner = saveOwner() ?? (local ? 'guest' : null);
  if (!account) {
    // This browser holds a signed-out account's save: it stays with that account (in its cloud).
    if (owner && owner !== 'guest') { await setAside(store, mainKey); await store.clear(); setSaveOwner(null); return undefined; }
    if (local) setSaveOwner('guest');
    return local;
  }
  const remote = await cloud.download();
  if (!remote) return local; // cloud unreachable: play on locally, uploads stay off this session
  const choice = decideBootSave({
    uid: account.uid,
    local: local ? { owner: owner ?? 'guest', lastSeen: local.lastSeen, bestStage: local.bestStage } : null,
    cloud: remote,
  });
  const keep = choice === 'ask' ? await accountUi.askWhichSave(local!.bestStage, remote.level) : choice;
  setSaveOwner(account.uid);
  if (keep === 'local') return local;
  if (local) await setAside(store, mainKey);
  if (keep === 'fresh' || !remote.json) return undefined;
  try { return deserialize(remote.json, Date.now()); } catch (e) { console.warn('Cloud save unreadable, keeping local:', e); return local; }
}

async function boot(): Promise<void> {
  const difficulty = readDifficulty();
  const keys = saveKeys(difficulty);
  const store = new BackedUpSave(new LocalSaveBackend(keys.main), keys.backup);

  // Accounts, cloud save and purchases (Tiny Legends' Firebase project and Worker; deploy/README.md).
  const accounts = new Accounts();
  let game!: Game;
  let resetting = false;
  const save = () => {
    if (resetting || !game) return;
    game.state.lastSeen = Date.now();
    void store.save(serialize(game.state));
    cloud.markDirty();
  };
  const cloud = new CloudSave(accounts, () => ({ json: serialize(game.state), level: game.state.bestStage, version: SAVE_VERSION }));
  let payments: Payments | null = null;
  const accountUi = new AccountUI(accounts, {
    // Boot again so the new account's cloud save is weighed against this device's.
    signedIn: () => { save(); resetting = true; location.reload(); },
    signOut: async () => {
      save();
      await cloud.flush();
      cloud.disable();
      payments?.stopWatching();
      resetting = true;
      await setAside(store, keys.main);
      await store.clear();
      setSaveOwner(null);
      await accounts.signOut();
      location.reload();
    },
  });
  const user = await Promise.race([accounts.ready(), new Promise<User | null>((r) => setTimeout(() => r(accounts.user), AUTH_WAIT_MS))]);
  const account = user && isVerifiedUser(user) ? user : null;
  const state = await chooseSave(await loadState(store, difficulty), account, cloud, accountUi, store, keys.main);
  game = new Game({ state, difficulty });

  const renderer = new Renderer(game, document.getElementById('canvas') as HTMLCanvasElement);
  new Sfx(() => game.state.settings.muted).bind(game);
  const ui = new UI(game, renderer, save, (d: DifficultyId) => {
    // Save this slot, remember the choice, and reload into the other difficulty's slot.
    save();
    try { localStorage.setItem(DIFFICULTY_KEY, d); } catch { /* storage blocked: stays on current */ }
    resetting = true;
    location.reload();
  });

  // Offline gains on load and whenever the tab comes back.
  const awardOffline = () => {
    const away = (Date.now() - game.state.lastSeen) / 1000;
    game.grantOffline(away);
    game.state.lastSeen = Date.now();
  };
  let meta: MetaUI | null = null;
  payments = new Payments(accounts, game, cloud, save, {
    credited: (gems) => {
      if (meta) celebrateGems(gems, () => game.state.cells, meta);
      else renderer.banner(`+${gems.toLocaleString()} Gems. Thank you for your purchase!`, '#7fe0ff', 20);
    },
    returned: () => renderer.banner('Confirming your payment…', '#ffcc4d', 17),
    notCompleted: () => renderer.banner('The payment was not completed. No money was taken.', '#ff8a8a', 17),
  });
  const gemStore = new GemStore(payments, accounts, accountUi, (text, color) => renderer.banner(text, color, 17));
  meta = new MetaUI(game, renderer, save, gemStore);
  if (account) {
    cloud.markDirty();
    void cloud.flush(); // an adopted or newer local save becomes the account's straight away
    payments.watchGrants();
    const returned = payments.consumeReturnOrder();
    if (returned) void payments.confirmReturn(returned);
  }
  const accountBtn = document.getElementById('account-btn');
  if (accountBtn) {
    accountBtn.addEventListener('click', () => void accountUi.openAccount());
    const refreshAccountDot = () => {
      const u = accounts.user;
      (accountBtn.querySelector('.dot') as HTMLElement).hidden = !!u && isVerifiedUser(u);
      accountBtn.title = u && isVerifiedUser(u) ? 'Your account' : 'Save your progress';
    };
    accounts.onChange(refreshAccountDot);
    refreshAccountDot();
  }
  // First launch: the intro story, then the terms. The intro is remembered in the save.
  void (async () => {
    if (!game.state.flags.introSeen) {
      await playIntro();
      game.state.flags.introSeen = true;
      save();
    }
    await accountUi.requireTerms();
  })();
  document.getElementById('intro-btn')?.addEventListener('click', () => void playIntro());
  awardOffline();
  ui.maybeRelicsIntro();
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) { save(); void cloud.flush(); }
    else { awardOffline(); last = performance.now(); }
  });
  window.addEventListener('pagehide', save);
  setInterval(save, SAVE_EVERY_MS);

  let last = performance.now();
  const frame = (t: number) => {
    // rAF timestamps can precede the last performance.now() read; clamp both ends.
    const dt = Math.max(0, Math.min(0.1, (t - last) / 1000));
    last = t;
    requestAnimationFrame(frame);
    try {
      game.update(dt);
      renderer.update(dt);
      renderer.draw();
    } catch (e) {
      console.error(e);
    }
  };
  requestAnimationFrame(frame);

  if (import.meta.env.DEV) {
    Object.assign(window, {
      TapHeroes: {
        game,
        give: (gold: number | string) => { game.state.gold = game.state.gold.add(gold); },
        stage: (n: number) => {
          game.state.stage = n; game.state.kills = 0; game.state.farming = false;
          game.state.bestStage = Math.max(game.state.bestStage, n);
          game.setState(game.state); ui.renderStage();
        },
        reset: async () => { resetting = true; await store.clear(); location.reload(); },
      },
    });
  }
}

void boot();
